import React, { useCallback, useState, useRef, useEffect } from "react";
import { Tooltip } from "@heroui/tooltip";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import { getLocationTypeIcon, toTitleCase } from "@/lib/general";
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { CalendarIcon } from "@heroicons/react/24/solid";
import { useSelectedDayItems } from "@/hooks/useSelectedDayItems";
import { HomeIcon } from "@heroicons/react/24/solid";

const CustomMarker = ({ locationDetails }: any) => {
  const getMarkerContent = () => {
    switch (locationDetails.type) {
      case "hotel":
        return (
          <div className="bg-white hover:bg-neutral-50 rounded-full ring-1 ring-neutral-700 hover:ring-neutral-800 w-7 h-7 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg">
            <HomeIcon className="w-3.5 h-3.5 text-neutral-700 group-hover:text-neutral-800 transition-colors" />
          </div>
        );
      case "home-stay":
        return (
          <div className="bg-white hover:bg-neutral-50 rounded-full ring-1 ring-neutral-600 hover:ring-neutral-700 w-7 h-7 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg">
            <i className="fa-solid fa-house text-neutral-600 text-[12px]"></i>
          </div>
        );
      default:
        return (
          <div className="bg-white hover:bg-indigo-50 rounded-full ring-1 ring-indigo-600 hover:ring-indigo-700 w-7 h-7 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg">
            <i
              className={`fa-solid ${
                locationDetails.categories &&
                locationDetails.categories.length > 0
                  ? getLocationTypeIcon(
                      toTitleCase(locationDetails.categories[0])
                    )
                  : "fa-map-pin"
              } text-indigo-600 text-[12px]`}
            ></i>
          </div>
        );
    }
  };

  return (
    <Marker
      longitude={locationDetails.coordinates.longitude}
      latitude={locationDetails.coordinates.latitude}
      onClick={() => {}}
      style={{ cursor: "pointer" }}
    >
      <Tooltip
        content={
          <div className="flex flex-row items-center justify-center gap-2 p-1">
            {locationDetails.type === "hotel" ? (
              <div className="flex items-center gap-2">
                <HomeIcon className="w-3.5 h-3.5 text-neutral-700 group-hover:text-neutral-800 transition-colors" />
                <p className="text-[13px] font-normal text-neutral-800 truncate max-w-[200px]">
                  {locationDetails?.name || ""}
                </p>
              </div>
            ) : locationDetails.type === "home-stay" ? (
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-house text-emerald-600 text-[12px]"></i>
                <p className="text-[13px] font-normal text-neutral-800 truncate max-w-[200px]">
                  {locationDetails?.name || ""}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <i
                  className={`fa-solid ${
                    locationDetails.categories &&
                    locationDetails.categories.length > 0
                      ? getLocationTypeIcon(locationDetails.categories[0])
                      : "fa-map-pin"
                  } text-indigo-600 text-[12px]`}
                ></i>
                <p className="text-[13px] font-normal text-neutral-800 truncate max-w-[200px]">
                  {locationDetails?.name || ""}
                </p>
              </div>
            )}
          </div>
        }
        onClick={() => {}}
        closeDelay={0}
        radius="md"
        showArrow={true}
        classNames={{
          content: "shadow-lg border border-neutral-100",
        }}
      >
        {getMarkerContent()}
      </Tooltip>
    </Marker>
  );
};

const MapView: React.FC = () => {
  const { setSelectedDay, getItineraryItems, selectedLocation } =
    useTripStore();
  const { overViewMapViewport, setOverViewMapViewport } = useTransportStore();
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [dayItems, setDayItems] = useState<any[]>([]);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const itineraryItems = getItineraryItems();
  const items = useSelectedDayItems(
    itineraryItems?.[selectedDayIndex]?.date || "",
    selectedLocation?.id
  );

  useEffect(() => {
    setDayItems(items);
  }, [items]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const onMove = useCallback(
    (evt: any) => setOverViewMapViewport(evt.viewState),
    []
  );

  const handleDayChange = (day: any, index: number) => {
    setSelectedDay(day.date);
    setSelectedDayIndex(index);

    setDayItems(day.items);
    setIsOpen(false);
  };

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  }, [dayItems]);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      className="w-full h-full mb-2 rounded-md overflow-hidden relative"
      data-tutorial="overview-map"
    >
      {/* Day selector dropdown */}
      <div className="absolute top-1.5 right-1.5 z-10">
        <div className="relative">
          <button
            onClick={handleToggleDropdown}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium bg-white/80 backdrop-blur-sm border border-neutral-400 hover:border-neutral-600 rounded-full transition-all duration-200 hover:bg-white group"
          >
            <CalendarIcon className="w-3.5 h-3.5 text-neutral-500 group-hover:text-neutral-700 transition-colors" />
            <span className="text-neutral-500 group-hover:text-neutral-700 transition-colors">
              Day {selectedDayIndex !== -1 ? selectedDayIndex + 1 : ""}
            </span>
            <i
              className={`fa-solid fa-chevron-${
                isOpen ? "up" : "down"
              } text-[9px] text-neutral-400 group-hover:text-neutral-600 transition-transform duration-200`}
            ></i>
          </button>

          {isOpen && (
            <div className="absolute top-10 right-0 w-44 bg-white/95 backdrop-blur-sm border border-neutral-200 rounded-xl shadow-lg py-1 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="max-h-[280px] overflow-y-auto no-scrollbar">
                {itineraryItems?.map((day: any, index: number) => (
                  <button
                    key={index}
                    className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-xs transition-colors
                      ${
                        selectedDayIndex === index
                          ? "bg-indigo-50/80 text-indigo-600"
                          : "text-neutral-600 hover:bg-neutral-50"
                      }`}
                    onClick={() => handleDayChange(day, index)}
                  >
                    <div
                      className={`w-1 h-1 rounded-full transition-colors
                      ${
                        selectedDayIndex === index
                          ? "bg-indigo-600"
                          : "bg-neutral-300"
                      }`}
                    />
                    <span className="font-medium">Day {index + 1}</span>
                    {day.items?.length > 0 && (
                      <span className="ml-auto text-[10px] font-normal text-neutral-400">
                        {day.items.length}{" "}
                        {day.items.length === 1 ? "stop" : "stops"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Map
        reuseMaps
        mapboxAccessToken={mapboxToken}
        ref={(ref) => (mapRef.current = ref && ref.getMap())}
        {...overViewMapViewport}
        onMove={onMove}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/jamiedennis8/clxmyy67600fs01pu0d6q2ha7/draft"
        attributionControl={false}
      >
        {dayItems.length > 0 &&
          dayItems.map((item: any, index: number) => (
            <Marker
              key={index}
              longitude={item.coordinates.longitude}
              latitude={item.coordinates.latitude}
            >
              <CustomMarker locationDetails={item} />
            </Marker>
          ))}
      </Map>
    </div>
  );
};

export default MapView;
